import { useMemo, useState } from 'react';
import { toast } from 'sonner';
import { ArrowRightLeft, Search } from 'lucide-react';
import { ModalShell } from '../ui/ModalShell';
import { Button } from '../ui/Button';
import { Pill } from '../ui/Pill';
import { ApiError } from '../../lib/api';
import {
  useTransferSources,
  useTransferPreview,
  useTransferTrainingPasses,
  type TransferPreview,
} from '../../hooks/onboardingOps';

/**
 * The duplicate-record transfer flow: pick the record the person's passes
 * accidentally landed on (usually an auto-provisioned learner record),
 * review which outstanding modules it can satisfy, give a reason, transfer.
 */
export function TransferTrainingModal({
  personId,
  open,
  onClose,
}: {
  personId: string;
  open: boolean;
  onClose: () => void;
}) {
  const sources = useTransferSources(open);
  const previewMutation = useTransferPreview(personId);
  const transfer = useTransferTrainingPasses(personId);

  const [query, setQuery] = useState('');
  const [preview, setPreview] = useState<TransferPreview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [reason, setReason] = useState('');

  const candidates = useMemo(() => {
    const q = query.trim().toLowerCase();
    const all = (sources.data ?? []).filter((p) => p.id !== personId);
    if (!q) return all.slice(0, 8);
    return all
      .filter((p) => p.full_name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q))
      .slice(0, 8);
  }, [sources.data, query, personId]);

  function reset() {
    setQuery('');
    setPreview(null);
    setSelected(new Set());
    setReason('');
  }

  async function pickSource(sourceId: string) {
    try {
      const p = await previewMutation.mutateAsync(sourceId);
      setPreview(p);
      // Everything transferable starts ticked; unmatched rows are not options.
      setSelected(new Set(p.candidates.filter((c) => c.source).map((c) => c.task_id)));
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Could not load the preview');
    }
  }

  async function onTransfer() {
    if (!preview) return;
    try {
      const res = await transfer.mutateAsync({
        source_person_id: preview.source_person.id,
        task_ids: [...selected],
        reason: reason.trim(),
      });
      const n = res.transferred.length;
      toast.success(
        n > 0
          ? `Transferred ${n} pass${n === 1 ? '' : 'es'} from ${preview.source_person.full_name}`
          : 'Nothing was transferred',
      );
      for (const s of res.skipped) toast.info(`${s.title}: ${s.reason}`);
      reset();
      onClose();
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : 'Transfer failed');
    }
  }

  const transferable = preview?.candidates.filter((c) => c.source) ?? [];
  const unmatched = preview?.candidates.filter((c) => !c.source) ?? [];

  return (
    <ModalShell
      open={open}
      onClose={() => {
        reset();
        onClose();
      }}
      titleId="transfer-training-title"
      maxWidth="max-w-lg"
      busy={transfer.isPending}
    >
      <h2 id="transfer-training-title" className="flex items-center gap-2 text-base font-semibold text-fg">
        <ArrowRightLeft className="h-4 w-4 text-accent" />
        Transfer training passes
      </h2>
      <p className="mt-1 text-sm text-fg-muted">
        For when this person's passes landed on a duplicate record (usually one auto-created by a
        first SSO login). Passes are copied across with a full audit trail; the duplicate record is
        not changed.
      </p>

      {!preview ? (
        <div className="mt-4">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-fg-dim" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search records by name or email"
              autoFocus
              className="h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface pl-9 pr-3 text-sm"
            />
          </div>
          <div className="mt-2 divide-y divide-divider/10 rounded-token-sm border border-line/10">
            {candidates.map((p) => (
              <button
                key={p.id}
                type="button"
                onClick={() => void pickSource(p.id)}
                disabled={previewMutation.isPending}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left text-sm hover:bg-surface-2"
              >
                <span className="min-w-0 truncate text-fg">{p.full_name}</span>
                <span className="shrink-0 text-xs text-fg-dim">{p.email}</span>
              </button>
            ))}
            {candidates.length === 0 ? (
              <div className="px-3 py-2 text-sm text-fg-dim">No matching records</div>
            ) : null}
          </div>
        </div>
      ) : (
        <div className="mt-4 space-y-3">
          <div className="text-sm text-fg-muted">
            From <span className="font-medium text-fg">{preview.source_person.full_name}</span>{' '}
            <button
              type="button"
              className="ml-1 text-xs text-accent underline-offset-2 hover:underline"
              onClick={() => setPreview(null)}
            >
              change
            </button>
          </div>

          {transferable.length === 0 ? (
            <p className="text-sm text-fg-muted">
              That record has no passing attempts matching this person's outstanding modules.
            </p>
          ) : (
            <div className="divide-y divide-divider/10 rounded-token-sm border border-line/10">
              {transferable.map((c) => (
                <label key={c.task_id} className="flex items-center gap-2.5 px-3 py-2 text-sm">
                  <input
                    type="checkbox"
                    checked={selected.has(c.task_id)}
                    onChange={(e) =>
                      setSelected((cur) => {
                        const next = new Set(cur);
                        if (e.target.checked) next.add(c.task_id);
                        else next.delete(c.task_id);
                        return next;
                      })
                    }
                  />
                  <span className="min-w-0 flex-1 truncate text-fg">{c.title}</span>
                  <Pill tone="success" size="sm">
                    {c.source!.score}% on {new Date(c.source!.completed_at).toLocaleDateString()}
                  </Pill>
                </label>
              ))}
            </div>
          )}

          {unmatched.length > 0 ? (
            <p className="text-xs text-fg-dim">
              No pass found for: {unmatched.map((c) => c.title).join(', ')}. These stay outstanding.
            </p>
          ) : null}

          <label className="block text-sm">
            <span className="font-medium text-fg-muted">Reason (recorded on the audit trail)</span>
            <input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="e.g. duplicate learner record from first SSO login"
              className="mt-1 h-10 w-full rounded-token-sm border border-line-strong/20 bg-surface px-3 text-sm"
            />
          </label>

          <div className="flex justify-end gap-2">
            <Button variant="ghost" size="sm" onClick={() => { reset(); onClose(); }}>
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={() => void onTransfer()}
              loading={transfer.isPending}
              disabled={selected.size === 0 || !reason.trim()}
            >
              Transfer {selected.size} pass{selected.size === 1 ? '' : 'es'}
            </Button>
          </div>
        </div>
      )}
    </ModalShell>
  );
}
